import { DOCUMENT, getLocaleTimeFormat } from '@angular/common';
import { AfterViewChecked, AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { DatePickerPresets, GlobalFunctions, imagePath, imgTypes } from '@global/GlobalFunctions';
import { Account, IAccount } from '@services/accounts.service';
import { AuthenticationService, IWebAppUser } from '@services/authentication.service';
import { OrganisationDataService, IOrganisation, Organisation, IOrganisationsHierarchy, OrganisationsHierarchy } from '@services/organisation-data.service';
import { combineLatest, takeUntil, distinctUntilChanged, filter, Subject, finalize, tap, toArray, interval, take, firstValueFrom } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AssetDataPoint, DataPoint, ISensorData } from '@services/datapoints-data.service';
import { convertEpochToDateString } from '@functions/functions';
import { ChartComponent, FontModel } from '@syncfusion/ej2-angular-charts';
import { Carousel, CarouselComponent, NodeExpandEventArgs, TreeViewComponent } from '@syncfusion/ej2-angular-navigations';
import { CarouselModule } from '@syncfusion/ej2-angular-navigations';
import { DashboardLayoutComponent, PanelModel } from '@syncfusion/ej2-angular-layouts';
import { regExpEscape } from '@ng-bootstrap/ng-bootstrap/util/util';
import { end, read } from '@popperjs/core';

@Component({
	selector: 'app-energy-dashboard',
	templateUrl: './energy-dashboard.component.html',
	styleUrls: ['./energy-dashboard.component.scss']
})
export class EnergyDashboardComponent implements AfterViewInit, OnInit, OnDestroy {

	/** Path to Assets Images */
	public imagePathAssets(): string {
		return imagePath(this.accountGUID, imgTypes.organisationassets).toLowerCase()
	}

	/** cellSpacing */
	public cellSpacing: number[] = [10, 10];

	/** aspectRatio */
	public aspectRatio: any = 100 / 82;

	/** mediaQuery */
	public mediaQuery: string = 'max-width: 800px';

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Selected Organisation ID */
	public organisationUID: string;

	/** Selected Organisation */
	public organisation: Organisation;

	/** accountID */
	public accountGUID: string;

	/** selectedAccount */
	public selectedAccount: IAccount;

	/** this user */
	public thisUser: IWebAppUser;

	/** parsedData */
	public parsedData: ISensorData[] = [];

	/** startDate */
	public startDate: Date;

	/** endDate */
	public endDate: Date;

	/** now */
	public now: Date = new Date();

	/** numDays */
	public numDays: number = 7;

	/** totalDifference */
	public totalDifference: number = 0;

	/** totalAvgRunning */
	public totalAvgRunning: number = 0;

	/** totalAvgPeriod */
	public totalAvgPeriod: number = 0;

	/** totalDeltaData */
	public totalDeltaData: ISensorData[] = [];

	/** totalCumulativeData */
	public totalCumulativeData: ISensorData[] = [];

	/** totalMax */
	public totalMax: number = 0;

	/** totalMin */
	public totalMin: number = 0;

	/** deltaMax */
	public deltaMax: number = 0;

	/** deltaMin */
	public deltaMin: number = 0;

	/** gaugeHeight */
	public gaugeHeight: string = "";

	/** deltaChartHeight */
	public deltaChartHeight: string = "";

	/** carouselHeight */
	public carouselHeight: string = "";

	/** imageHeight */
	public imageHeight: number = 0;

	/** carouselWidth */
	public carouselWidth: string = "";

	/** gaugeDiv */
	@ViewChild('gaugeDiv') gaugeDiv!: ElementRef<HTMLDivElement>;

	/** deltaChartDiv */
	@ViewChild('deltaChartDiv') deltaChartDiv!: ElementRef<HTMLDivElement>;

	/** assetCarouselDiv */
	@ViewChild('assetCarouselDiv') assetCarouselDiv!: ElementRef<HTMLDivElement>;

	/** imgInfoDiv */
	@ViewChildren('imgInfoDiv') imgInfoDivs!: QueryList<ElementRef<HTMLDivElement>>;

	/** legendSettings */
	public legendSettings: Object = {
		visible: true,
		position: 'Top',
		// location: { x: 20, y: 0 },
		width: '100%'
	};

	/** majorTicks */
	public majorTicks: Object = {
		height: 5,
		width: 1.5,
		interval: 0.5,
		offset: 0
	};

	/** minorTicks */
	public minorTicks: Object = {
		height: 5,
		width: 0.5,
		interval: 0.25,
		offset: 0
	};

	/** runningPointerColour */
	public runningPointerColour: string = "lightgreen";

	/** periodPointerColour */
	public periodPointerColour: string = "lightblue";

	/** deltaSeriesColour */
	public deltaSeriesColour: string = "lightseagreen"

	/** currentPointerColour */
	public currentPointerColour: string = "#ee95e7ff"

	/** targetSI */
	public targetSI: string = "kWh";

	/** strDuration */
	public strDuration: string = "07:00:00";

	/** maxMonths */
	private maxMonths: number = 36; // max months increased as retrieving data for long periods no longer an issue

	/** DAY */
	public DAY: number = (1 * 24 * 60 * 60 * 1000);

	/** Chart Legend */
	public legend: Object;

	/** Chart Zoom Settings */
	public zoomSettings: Object;

	/** crosshair */
	public crosshair: object = {
		enable: true,
		lineType: 'Vertical'
	};

	/** Sensor pen Pallet Colours */
	public palette: object;

	/** Tooltip Settings */
	public tooltipSettings: Object;

	/** Chart boarder Settings */
	public borderSettings: Object;

	/**
	 * Chart Selection mode
	 * None|Point|Series|Cluster|DragXY|DragX|DragY
	 */
	public selectionMode: string;

	/** sumDeltaChart */
	@ViewChild('sumDeltaChart') sumDeltaChart!: ChartComponent;

	/** sumDeltaChart */
	@ViewChild('sumTotalChart') sumTotalChart!: ChartComponent;

	/** assetCarousel */
	@ViewChild('assetCarousel') assetCarousel!: CarouselComponent;

	/** dashboardLayout */
	@ViewChild('dashboardLayout') dashboardLayout!: DashboardLayoutComponent;

	/** Sensor X Axis */
	public sensorXAxis: Object;

	/** titleStyle */
	public titleStyle: FontModel = {
		fontWeight: "bold",
		size: "large"
	}

	/** recentRunningValue */
	public recentRunningValue: number = 0;

	/** energyAssets */
	public energyAssets: { Asset: LevelAsset, Datapoints: AssetDataPoint[] }[] = [];

	/** selectedCarouselIndex */
	public selectedCarouselIndex: number = 0;

	/** autoPlay */
	public autoPlay: boolean = true;

	/** regex */
	public regex = /^\[[0-9]*\]$/;

	/** regexDelta */
	public regexDelta = /^\[[0-9]*_DELTA\]$/;

	/** regexDelta */
	public regexCalc = /^\[[0-9]*_CALCULATED\]$/;

	/** assetRelevantData */
	public assetRelevantData: { AssetID: number, RelevantData: ISensorData[], RelevantDeltaData: ISensorData[] }[] = [];

	/** assetEnergyMetrics */
	public assetEnergyMetrics: { AssetID: number, Current: number, Avg: number, RunningAvg: number, Total: number, Diff: number }[] = [];

	/** dataFetchInterval */
	private dataFetchInterval: any;

	/** Page Refresh Time in Minutes */
	private refreshTime: number = 0.5 // Minutes

	/** TargetDataPoints */
	public TargetDataPoints: AssetDataPoint[] = [];

	/** calculatedData */
	public calculatedData: ISensorData[] = [];

	/** totalCaluclatedData */
	public totalCaluclatedData: ISensorData[] = [];

	/** isKWHDashboard - if true a calculation is performed on the delta data */
	public isKWHDashboard: boolean = true;

	/** runningThreshold */
	public runningThreshold: number = 0.05;

	/** showDeltaChartBars */
	public showDeltaChartBars: boolean = false;

	/** deltaBarData */
	public deltaBarData: ISensorData[] = [];

	/** energyStructure */
	public energyStructure: IOrganisationsHierarchy[] = [];

	/** field */
	public fields: Object

	/** treeview */
	@ViewChild('treeview') treeview!: TreeViewComponent;

	/** selectedLocation */
	public selectedLocation: IOrganisationsHierarchy;

	/** includedDatapoints */
	public includedDatapoints: AssetDataPoint[] = [];

	/** fetching */
	public fetching: boolean = false;

	/** justFetchedDatapoints */
	public justFetchedDatapoints: AssetDataPoint[] = [];

	/** panels */
	@ViewChildren('e-panel') panels!: QueryList<ElementRef<HTMLDivElement>>;

	/** today - #71 */
	public today: Date = new Date();

	/** datePickerPresets - #71 */
	public datePickerPresets = DatePickerPresets;

	/**
	 * Default Constructor
	 * @param auth auth
	 * @param organisationDataService OrganisationData
	 * @param globalFunctions globalFunctions
	 * @param router router
	 * @param route route
	 * @param siteSubscriptions siteSubscriptions
	 * @param document document
	 */
	constructor(
		public auth: AuthService,
		public organisationDataService: OrganisationDataService,
		public globalFunctions: GlobalFunctions,
		public router: Router,
		private route: ActivatedRoute,
		private siteSubscriptions: siteSubscriptions,
		private authenticationService: AuthenticationService,
		private assetsDataService: AssetsDataService,
		@Inject(DOCUMENT) public document: Document,
	) {

		/** Get required subscriptions */
		combineLatest([
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([currentUser, paramMap]) =>
				!!currentUser && paramMap.has('organisation'))
		).subscribe({
			next: async ([currentUser, paramMap]) => {

				let [selectedAccount, selectedAccountGUID, selectedOrganisation, selectedOrganisationUID] = await firstValueFrom(
					combineLatest([
						this.siteSubscriptions.selectedAccount$.pipe(take(1)),
						this.siteSubscriptions.selectedAccountGUID$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisation$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisationUID$.pipe(take(1))
					]
					)
				);

				this.thisUser = currentUser

				this.selectedAccount = selectedAccount;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
				this.accountGUID = this.selectedAccount.GUID;

				this.organisation = <IOrganisation>selectedOrganisation;
				this.organisationUID = this.organisation.Uid;

				let urlAccountID = this.router.url.split('/')[1];

				if (this.accountGUID.toLowerCase() != urlAccountID.toLowerCase() && this.router.url.includes('energy-dashboard') && !this.router.url.includes('routing#')) {
					this.router.navigateByUrl(`routing#${this.router.url}`);
				}

				this.endDate = new Date();
				this.startDate = new Date();
				this.startDate.setDate(this.endDate.getDate() - this.numDays);

				this.zoomSettings = {
					mode: 'X',
					// enableMouseWheelZooming: true,
					enablePinchZooming: true,
					enableSelectionZooming: true,
					enableScrollbar: true
				};

				this.legend = { visible: true };
				this.tooltipSettings = {
					enable: true,
					//shared: true // Enables shared tooltip
				};
				this.selectionMode = 'DragX';

				this.getEnergyAssets();

				this.getOrganisationEnergyStructure();
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}
	/**
 * Page On Destroy Event Handler
 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
			this.dataFetchInterval.unsubscribe();
			clearInterval(this.dataFetchInterval);
			// if (this.resizeObserver) {
			// 	this.resizeObserver.unobserve(this.imageElement.nativeElement);
			// }
		}
		catch { }

	}

	/** ngOnInit */
	ngOnInit(): void {
		this.setXAxis();
		this.dataFetchInterval = interval((this.refreshTime * 60000)).pipe(takeUntil(this.finalise)).subscribe(() => {
			if (!this.fetching) {
				this.endDate = new Date();
				this.startDate = new Date();
				if (this.numDays >= 1) {
					this.startDate.setDate(this.endDate.getDate() - this.numDays);
				} else {
					this.startDate.setHours(this.endDate.getHours() - this.numDays * 24);
				}
				this.GetEnergyData(this.startDate.getTime(), this.endDate.getTime(), true);
			}
		});

		this.siteSubscriptions.redrawDashboard$.pipe(
			takeUntil(this.finalise), distinctUntilChanged(),
			filter((redraw) => !!redraw)
		).subscribe({
			next: () => {
				this.redrawDashboard();
				this.siteSubscriptions.RedrawDashboard(false);
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in redrawDashboard$", false);
			}
		});
	}

	/**
	 * ngAfterViewInit
	 */
	ngAfterViewInit(): void {
		if (this.gaugeDiv) {
			this.setGaugeHeight();
		}
		if (this.deltaChartDiv) {
			this.setDeltaChartHeight();
		}
		if (this.assetCarouselDiv) {
			this.setCarouselHeight();
		}
		this.checkAndSetImgHeight();
		this.selectedCarouselIndex = 0;

		setTimeout(() => {
			this.redrawDashboard();
		}, 1000);
	}

	/**
	 * checkAndSetImgHeight
	 */
	checkAndSetImgHeight() {
		if (this.imgInfoDivs.length > 0) {
			this.setImgHeight();
		} else {
			setTimeout(() => {
				this.checkAndSetImgHeight();
			}, 10);
		}
	}

	/**
	 * Get Values for the DataPoints
	 * @param asset
	 * @param dateStart
	 * @param dateEnd
	 * @returns
	 */
	async GetEnergyData(dateStart: any, dateEnd: any, noSpinner: boolean = false, datapointIDs: string = ""): Promise<boolean> {
		if (!noSpinner) {
			this.globalFunctions.displaySpinner(true);
		}

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.fetching = true;

		this.justFetchedDatapoints = datapointIDs == "" ? this.TargetDataPoints : this.includedDatapoints;

		return new Promise<boolean>((resolve, reject) => {
			this.organisationDataService.getOrganisationEnergyData$(this.accountGUID, this.organisationUID, dateStart, dateEnd, datapointIDs).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				// filter((dataPointValues) => !!dataPointValues),
				tap(() => {
				}),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
					this.globalFunctions.displaySpinner(false);
				})
			).subscribe({
				next: async (dataPointValues: any) => {
					try {
						let parsedData: ISensorData[]
						parsedData = dataPointValues.map((dataPointValue: any) => {
							return {
								//AssetId: dataPointValue.AssetID,
								// AssetId: 0,
								timestamp: dataPointValue.Timestamp,
								sampleDate: new Date(dataPointValue.Timestamp),
								...dataPointValue.Values,
								...Object.assign(
									{},
									...Object.entries(dataPointValue.DeltaValues).map(([key, value]: any) => {
										return { [key.replace("]", "") + "_DELTA" + "]"]: value };
									})
								),
								...Object.assign(
									{},
									...Object.entries(dataPointValue.DeltaTimestamps).map(([key, value]: any) => {
										return { [key.replace("]", "") + "_DELTA_TIME" + "]"]: value };
									})
								)
							}
						});
						console.log("Parsed DataPoints");

						this.formatAllData(parsedData);

						this.fetching = false;
						resolve(true);
					} catch (error) {
						console.error(error);
						reject(true);
					}
				},
				error: (error) => {
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "Error: this.organisationDataService.getOrganisationEnergyData$", false);
					}
					reject(true);
				}
			});
		});
	}

	/**
	 * formatAllData
	 * @param parsedData parsedData  
	 */
	formatAllData(parsedData: ISensorData[]) {
		this.parsedData = parsedData;

		// reset values 
		this.resetValues();

		// work this out first if we need to
		if (this.isKWHDashboard) {
			this.TargetDataPoints.forEach(datapoint => {
				this.calculateKWHData(datapoint.DataPointID);
			});
		}

		const FIVE_MINUTES = (1000 * 60 * 5);
		const TEN_MINUTES = FIVE_MINUTES * 2;
		const HOUR = TEN_MINUTES * 6;

		// Getting total Delta / Calculated Values		
		let dataForBars: ISensorData[] = this.getAllDataForRegex(this.parsedData, this.regexDelta, "DELTA");
		this.calculateDeltaChartBarData(dataForBars, "DELTA");

		if (!this.isKWHDashboard) {
			this.totaliseDeltaData(TEN_MINUTES);
			this.calculateDeltaMinMax();
			this.getLatestValue(this.totalDeltaData, "Total_DELTA"); // Getting recent running value
		}
		else {
			this.totaliseCalculatedData(TEN_MINUTES);
			this.setCalculatedAverages();
			this.getLatestValue(this.totalCaluclatedData, "Total_CALCULATED"); // Getting recent running value
		}

		// Getting total Cumulative Values
		this.totaliseCumulativeData(HOUR);
		// const formattedData = this.getAllDataForRegex(this.parsedData, this.regex, "ACCRUED");
		// this.totalCumulativeData = this.totaliseData(formattedData, HOUR, "ACCRUED", "Total");

		// Getting total difference, max and min
		this.calculateTotalValues();

		// Getting Period and Running Averages, delta max and min
		this.calculateDeltaAverages();

		// Trend the total delta values and total cumulative values		
		if (this.sumDeltaChart) {
			this.sumDeltaChart.clearSeries();
		}
		if (this.sumTotalChart) {
			this.sumTotalChart.clearSeries();
		}

		//TODO: add si check or something here for other calculations
		if (!this.showDeltaChartBars) {
			if (!this.isKWHDashboard) {
				this.trendDeltaValues();
			} else {
				this.trendkWHValues();
			}
		} else {
			this.trendDeltaBars();
		}

		this.calculateGaugeTicks();

		if (this.energyAssets.length > 0) {
			this.energyAssets.forEach((x) => {
				if (this.checkEnergyAssetDatapointsIncluded(x)) {
					this.getAssetRelevantData(x);
				}
			});
		}
		if (this.assetRelevantData.length > 0) {
			this.assetRelevantData.forEach((x) => this.assetEnergyMetrics.push(this.calculateAsssetEnergyMetrics(x.AssetID)));
		}
	}

	/**
	 * setGaugeHeight
	 */
	setGaugeHeight() {
		const observer = new ResizeObserver(entries => {
			for (const entry of entries) {
				const height = entry.contentRect.height;
				this.gaugeHeight = `${height * 0.85}px`
			}
		});

		observer.observe(this.gaugeDiv.nativeElement);
	}

	/**
	 * setGaugeHeight
	 */
	setDeltaChartHeight() {
		const observer = new ResizeObserver(entries => {
			for (const entry of entries) {
				const height = entry.contentRect.height;
				this.deltaChartHeight = `${height * 0.9}px`
			}
		});

		observer.observe(this.deltaChartDiv.nativeElement);
	}

	/**
	 * setGaugeHeight
	 */
	setCarouselHeight() {
		const observer = new ResizeObserver(entries => {
			for (const entry of entries) {
				const height = entry.contentRect.height;
				const width = entry.contentRect.width;
				this.carouselHeight = `${height * 0.95}px`
				this.carouselWidth = `${width * 0.97}px`
			}
		});

		observer.observe(this.assetCarouselDiv.nativeElement);
	}

	/**
	 * setGaugeHeight
	 */
	setImgHeight() {
		const observer = new ResizeObserver(entries => {
			for (const entry of entries) {
				const height = entry.contentRect.height;
				this.imageHeight = height;
			}
		});

		observer.observe(this.imgInfoDivs.first.nativeElement);
	}

	/**
	 * convertEpoch
	 * @param epoch epoch 
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * DatePickerChange
	 * @param event event 
	 */
	DatePickerChange(event: any) {
		try {
			const date1 = new Date(event.startDate.getTime());
			const date2 = new Date(event.endDate.getTime());

			const diffInMs = Math.abs(date2.getTime() - date1.getTime());
			const diffInMonths = diffInMs / (1000 * 60 * 60 * 24 * 30.44);

			if (diffInMonths < this.maxMonths) {
				this.startDate = event.startDate;
				this.endDate = event.endDate

				if (this.startDate && this.endDate) {
					/** Add a day to retrieve data for last day too */
					this.endDate.setDate(this.endDate.getDate() + 1)

					/** Reset Duration Search */
					this.strDuration = "custom";

					this.numDays = parseFloat(((this.endDate.getTime() - this.startDate.getTime()) / this.DAY).toFixed(2));

					this.GetEnergyData(this.startDate.getTime(), this.endDate.getTime());
				}
			}
		}
		catch (error) { this.globalFunctions.HandleError(error, "DatePickerChange Error: Unknown", false) }
	}

	/**
	 * selectNumDays
	 * @param event event 
	 */
	selectNumDays(sDuration: string) {
		this.strDuration = sDuration

		let parts: any[] = this.strDuration.split(":")
		this.endDate = new Date()
		this.startDate = new Date()
		this.startDate.setDate(this.startDate.getDate() - parts[0]);
		this.startDate.setHours(this.startDate.getHours() - parts[1]);
		this.startDate.setMinutes(this.startDate.getMinutes() - parts[2]);

		this.numDays = parseInt(parts[0]) || parseFloat((parseInt(parts[1]) / 24).toFixed(2));

		this.GetEnergyData(this.startDate.getTime(), this.endDate.getTime());
	}

	/**
	 * trendDeltaValues
	 */
	trendDeltaValues() {
		if (this.sumDeltaChart) {
			this.sumDeltaChart.clearSeries();
			this.sumDeltaChart.addSeries([{
				animation: {
					enable: false
				},
				emptyPointSettings: { mode: "Drop" },
				type: 'Line',
				dataSource: this.totalDeltaData,
				name: "Total Running",
				xName: 'timestamp',
				yName: `Total_DELTA`,
				marker: {
					visible: false,
					width: 8,
					height: 8,
					shape: 'Triangle',
					dataLabel: { "name": 'x' },
					isFilled: true
				},
				// yAxisName: `Axis${this.targetSI}`,
				fill: this.deltaSeriesColour
			}]);
		}
	}

	/**
	 * trendkWHValues 
	 */
	trendkWHValues() {
		if (this.sumDeltaChart) {
			this.sumDeltaChart.clearSeries();
			this.sumDeltaChart.addSeries([{
				animation: {
					enable: false
				},
				emptyPointSettings: { mode: "Drop" },
				type: 'Line',
				dataSource: this.totalCaluclatedData,
				name: "Total Usage (kWh)",
				xName: 'timestamp',
				yName: `Total_CALCULATED`,
				marker: {
					visible: false,
					width: 8,
					height: 8,
					shape: 'Triangle',
					dataLabel: { "name": 'x' },
					isFilled: true
				},
				// yAxisName: `Axis${this.targetSI}`,
				fill: this.deltaSeriesColour,
				visible: true
			}]);
		}
	}

	/**
	 * trendDeltaBars
	 */
	trendDeltaBars() {
		if (this.sumDeltaChart) {
			this.sumDeltaChart.clearSeries();
			if (this.deltaBarData.length > 0) {
				this.sumDeltaChart.addSeries([{
					animation: {
						enable: false
					},
					emptyPointSettings: { mode: "Drop" },
					type: 'Column',
					dataSource: this.deltaBarData,
					name: "Total Usage (kWh)",
					xName: 'timestamp',
					yName: `BAR_DATA`,
					marker: {
						visible: false,
						width: 8,
						height: 8,
						shape: 'Triangle',
						dataLabel: { "name": 'x' },
						isFilled: true
					},
					// yAxisName: `Axis${this.targetSI}`,
					fill: this.deltaSeriesColour,
					columnWidth: 1,
					columnSpacing: 0,
					visible: true
				}]);
			}
		}
	}

	/**
	 * trendTotalValues
	 */
	trendTotalValues() {
		if (this.sumTotalChart) {
			this.sumTotalChart.addSeries([{
				animation: {
					enable: false
				},
				emptyPointSettings: { mode: "Drop" },
				type: 'Line',
				dataSource: this.totalCumulativeData,
				name: "Total Cumulative",
				xName: 'timestamp',
				yName: `Total`,
				marker: {
					visible: false,
					width: 8,
					height: 8,
					shape: 'Triangle',
					dataLabel: { "name": 'x' },
					isFilled: true
				},
				// yAxisName: `Axis${this.targetSI}`,
				fill: this.deltaSeriesColour
			}])
		}
	}

	/**
	 * Add a section to the graph
	 * @param lines stripLine Definition
	 */
	setXAxis(lines: object[] = []): void {

		/* Sensor Graph specific variables */
		this.sensorXAxis = {
			// title: 'Date',
			valueType: "DateTime", //'Category',
			intervalType: parseInt(this.strDuration.split(":")[0]) > 2 || this.strDuration == 'custom' ? "Days" : "Hours",
			// intervalType: "Hours",
			// interval: 1,
			labelFormat: parseInt(this.strDuration.split(":")[0]) > 2 || this.strDuration == 'custom' ? "dd-MMM" : "HH:mm dd-MMM",
			// labelFormat: "HH:mm:ss dd-MMM",
			// labelIntersectAction: "Rotate45",
			// labelRotation: 35,
			// labelRotation: 90,
			majorTickLines: { visible: false },
			minorTickLines: { visible: false },
			majorGridLines: { visible: false },
			minorGridLines: { visible: false },
			// stripLines: lines

		};
	}

	/**
	 * getEnergyAssets
	 */
	getEnergyAssets() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.getEnergyAssets$(this.accountGUID, this.organisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((invites) => !!invites),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (assets) => {
				this.energyAssets = assets
					.map((x: { Asset: string, Datapoints: string }) => ({
						Asset: JSON.parse(x.Asset),
						Datapoints: JSON.parse(x.Datapoints)
					}));

				this.energyAssets.forEach((x) => this.TargetDataPoints.push(...x.Datapoints));
				this.GetEnergyData(this.startDate.getTime(), this.endDate.getTime());
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.assetsDataService.getEnergyAssets$:", error);
				}
			},
		});
	}

	/**
	 * redrawDashboard
	 */
	redrawDashboard() {
		if (this.dashboardLayout) {
			this.dashboardLayout.refresh();
		}
	}

	/**
	 * getDatapointDPTitles
	 * @param datapoints datapoints 
	 * @returns 
	 */
	getDatapointDPTitles(datapoints: AssetDataPoint[]): string {
		return datapoints.map((x: DataPoint) => x.DPTitle).toString();
	}

	/**
	 * getAsssetEnergyMetrics
	 * @param energyAsset energyAsset 
	 * @returns 
	 */
	getAssetRelevantData(energyAsset: { Asset: LevelAsset, Datapoints: AssetDataPoint[] }) {
		if (this.parsedData.length > 0) {
			let relevantData: ISensorData[] = [];
			let relevantDeltaData: ISensorData[] = [];
			this.parsedData.map((x: ISensorData) => {
				Object.entries(x).forEach(item => {
					if (this.regex.test(item[0]) && energyAsset.Datapoints.map((x: DataPoint) => `[${x.DataPointID}]`).includes(item[0])) {
						relevantData.push({
							timestamp: x.timestamp,
							sampleDate: x.sampleDate.toString(),
							[item[0]]: item[1]
						});
					} else if (this.regexDelta.test(item[0]) && energyAsset.Datapoints.map((x: DataPoint) => `[${x.DataPointID}_DELTA]`).includes(item[0])) {
						relevantDeltaData.push({
							timestamp: x.timestamp,
							sampleDate: x.sampleDate.toString(),
							[item[0]]: item[1]
						});
					}
				});
			});
			this.assetRelevantData.push({ AssetID: energyAsset.Asset.AssetID, RelevantData: relevantData, RelevantDeltaData: relevantDeltaData });
		}
	}

	/**
	 * getAsssetEnergyMetrics
	 * @param assetID assetID 
	 * @returns 
	 */
	calculateAsssetEnergyMetrics(assetID: number): { AssetID: number, Current: number, Avg: number, RunningAvg: number, Total: number, Diff: number } {
		let result: { AssetID: number, Current: number, Avg: number, RunningAvg: number, Total: number, Diff: number } = {
			AssetID: assetID,
			Current: 0,
			Avg: 0,
			RunningAvg: 0,
			Total: 0,
			Diff: 0
		}
		if (this.assetRelevantData.length > 0) {
			let data = this.assetRelevantData.find((x) => x.AssetID === assetID);
			if (data) {
				let totalCurrent: number = 0;
				let totalAvg: number = 0;
				let numValues: number = 0;
				let totalRunningAvg: number = 0;
				let numRunningValues: number = 0;
				let totalTotal: number = 0;
				let totalDiff: number = 0;
				let totalEnd: number = 0;
				let totalStart: number = 0;
				for (let i: number = 0; i < data.RelevantDeltaData.length; i++) {
					Object.entries(data.RelevantDeltaData[i]).forEach(item => {
						if (i == (data!.RelevantDeltaData.length - 1)) {
							if (this.regexDelta.test(item[0])) {
								totalCurrent += <number>item[1];
							}
						}
						if (this.regexDelta.test(item[0])) {
							const value = <number>item[1];
							numValues++;
							totalAvg += value;
							if (value > this.runningThreshold) {
								numRunningValues++;
								totalRunningAvg += value;
							}
						}
					});
				}
				totalCurrent = parseFloat(totalCurrent.toFixed(2));
				totalAvg = parseFloat((totalAvg / (numValues || 1)).toFixed(2));
				totalRunningAvg = parseFloat((totalRunningAvg / (numRunningValues || 1)).toFixed(2));
				Object.entries(data.RelevantData[data.RelevantData.length - 1]).forEach(item => {
					if (this.regex.test(item[0])) {
						const value = <number>item[1];
						totalTotal += value;
						totalEnd += value;
					}
				});
				Object.entries(data.RelevantData[0]).forEach(item => {
					if (this.regex.test(item[0])) {
						const value = <number>item[1];
						totalStart += value;
					}
				});
				totalTotal = parseFloat(totalTotal.toFixed(2));
				totalDiff = parseFloat((totalEnd - totalStart).toFixed(2));
				result.Current = totalCurrent;
				result.Avg = totalAvg;
				result.RunningAvg = totalRunningAvg;
				result.Total = totalTotal;
				result.Diff = totalDiff;
			}
		}
		return result;
	}

	/**
	 * getAsssetEnergyMetrics
	 * @param assetID assetID 
	 * @returns 
	 */
	getAsssetEnergyMetrics(assetID: number): { AssetID: number, Current: number, Avg: number, RunningAvg: number, Total: number, Diff: number } {
		let result: { AssetID: number, Current: number, Avg: number, RunningAvg: number, Total: number, Diff: number } = {
			AssetID: 0,
			Current: 0,
			Avg: 0,
			RunningAvg: 0,
			Total: 0,
			Diff: 0
		}
		if (this.assetEnergyMetrics.length > 0) {
			let _result = this.assetEnergyMetrics.find((x) => x.AssetID === assetID);
			if (_result) {
				result = _result;
			}
		}
		return result;
	}

	/**
	 * goToAsset
	 */
	goToAsset(asset: LevelAsset) {
		this.siteSubscriptions.SetSelectedAsset(asset);
		this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/location/${asset.ParentUID}/asset/${asset.AssetID}`]);
	}

	/**
	 * calculateGuageTicks
	 */
	calculateGaugeTicks() {
		let interval = parseFloat(((this.deltaMax - this.deltaMin) / 10).toFixed(2));
		this.majorTicks = {
			height: 5,
			width: 1.5,
			interval: interval,
			offset: 0
		};
		this.minorTicks = {
			height: 2.5,
			width: 1.5,
			interval: interval / 2,
			offset: 0
		};
	}

	/**
	 * calculateKWHData
	 */
	calculateKWHData(datapointID: number) {
		for (let i: number = 1; i < this.parsedData.length; i++) {
			const data = this.parsedData[i];
			const kWhValue = this.getUsageValue(data, datapointID);
			if (kWhValue != undefined) {
				this.calculatedData.push({
					timestamp: data.timestamp,
					sampleDate: data.sampleDate,
					[`[${datapointID}_CALCULATED]`]: kWhValue
				});
			}
		}
	}

	/**
	 * getUsageValue
	 * @param data data 
	 * @returns 
	 */
	getUsageValue(data: ISensorData, datapointID: number): number | undefined {
		let result = undefined;
		let deltaTimestamp = data[`[${datapointID}_DELTA_TIME]`];
		let deltaValue = data[`[${datapointID}_DELTA]`];
		let factor: number = 60 * 60 * 1000; //hour factor
		if (deltaTimestamp && (deltaValue != undefined)) {
			result = (factor / (<number>deltaTimestamp)) * <number>deltaValue;
		}
		return result;
	}

	/**
	 * getKWHDataMaxMin
	 * @returns 
	 */
	getKWHDataMaxMin(): { max: number, min: number, avg: number, runningAvg: number } {
		let result = { max: 0, min: 999999, avg: 0, runningAvg: 0 };
		let valueCount: number = 0;
		let runningValueCount: number = 0;
		this.totalCaluclatedData.forEach(item => {
			const value = <number>item["Total_CALCULATED"];
			if (value != undefined) {
				valueCount++;
				runningValueCount = value > this.runningThreshold ? runningValueCount += 1 : runningValueCount;
				result = {
					max: value > result.max ? value : result.max,
					min: value < result.min ? value : result.min,
					avg: result.avg += value,
					runningAvg: value > this.runningThreshold ? result.runningAvg += value : result.runningAvg
				}
			}
		});
		result.avg /= valueCount;
		result.runningAvg /= runningValueCount;
		return result;
	}

	/**
	 * calculateTotalValues
	 */
	calculateTotalValues_Old() {
		let totalStart: number = 0;
		let totalEnd: number = 0;
		let seenDPsStart: string[] = [];
		let seenDPsEnd: string[] = [];
		for (let i: number = 0; i < 10; i++) {
			Object.entries(this.parsedData[i]).forEach(item => {
				if (this.regex.test(item[0]) && !seenDPsStart.includes(item[0])) {
					seenDPsStart.push(item[0]);
					totalStart += <number>item[1];
				}
			});
			Object.entries(this.parsedData[this.parsedData.length - (i + 1)]).forEach(item => {
				if (this.regex.test(item[0]) && !seenDPsEnd.includes(item[0])) {
					seenDPsEnd.push(item[0]);
					totalEnd += <number>item[1];
				}
			});
		}
		this.totalDifference = parseFloat((totalEnd - totalStart).toFixed(3));
		this.totalMax = parseFloat(totalEnd.toFixed(3));
		this.totalMin = parseFloat(totalStart.toFixed(3));
	}

	/**
	 * calculateTotalValues
	 */
	calculateTotalValues() {
		this.totalMin = parseFloat((<number>this.totalCumulativeData[0]["Total"] ?? 0).toFixed(2));
		this.totalMax = parseFloat((<number>this.totalCumulativeData[this.totalCumulativeData.length - 1]["Total"] ?? 0).toFixed(2));
		this.totalDifference = parseFloat((this.totalMax - this.totalMin).toFixed(2));
	}

	/**
	 * calculateDeltaAverages
	 */
	calculateDeltaAverages_old() {
		let numTotal: number = 0;
		let numRunning: number = 0;
		this.parsedData.forEach(x => {
			Object.entries(x).forEach(item => {
				if (this.regexDelta.test(item[0])) {
					const value = <number>item[1];
					this.totalAvgPeriod += value;
					numTotal++;
					if (value > this.runningThreshold) {
						this.totalAvgRunning += value;
						numRunning++;
					}
				}
			});
		});
		this.totalAvgPeriod = parseFloat((this.totalAvgPeriod / numTotal).toFixed(3));
		this.totalAvgRunning = parseFloat((this.totalAvgRunning / numRunning).toFixed(3));
	}

	/**
	 * calculateDeltaAverages
	 */
	calculateDeltaAverages() {
		this.totalAvgPeriod = parseFloat((this.totalDifference / (this.numDays * 24)).toFixed(3));
		this.totalAvgRunning = parseFloat((this.totalDifference / this.getRunningHours(this.parsedData)).toFixed(3));
	}

	/**
	 * getRunningHours
	 * @param data data 
	 * @returns 
	 */
	getRunningHours(data: ISensorData[]): number {
		let result: number = 0;
		const DAY = (24 * 60 * 60 * 1000);
		const HOUR = DAY / 24;
		for (let i: number = 0; i < (this.numDays * 24); i++) {
			const start = this.startDate.getTime() + (HOUR * i);
			const end = start + HOUR;
			result = data.filter((x: ISensorData) => (x.timestamp > start && x.timestamp <= end)
				&& this.checkRunning(x)).length > 0 ? result + 1 : result;
		}
		return result == 0 ? 1 : result;
	}

	/**
	 * checkRunning
	 * @param data data 
	 * @returns 
	 */
	checkRunning(data: ISensorData): boolean {
		return Object.entries(data).some(x => this.regexDelta.test(x[0]) && <number>x[1] > this.runningThreshold);
	}

	/**
	 * totaliseDeltaData
	 * @param FIVE_MINUTES FIVE_MINUTES  
	 */
	totaliseDeltaData(FIVE_MINUTES: number) {
		for (let start: number = this.startDate.getTime(); start < this.endDate.getTime(); start += FIVE_MINUTES) {
			const end = start + FIVE_MINUTES;
			let data = this.parsedData.filter((x: ISensorData) => x.timestamp < end && x.timestamp > start);
			let avgValues: [string, number, number][] = [];
			data.forEach((y: ISensorData) => {
				Object.entries(y).forEach(item => {
					if (this.regexDelta.test(item[0])) {
						let existingItem = avgValues.find(av => av[0] == item[0]);
						if (existingItem) {
							existingItem[1] += <number>item[1];
							existingItem[2]++;
						} else {
							avgValues.push([item[0], <number>item[1], 1]);
						}
					}
				});
			});
			let timestamp = end - ((end - start) / 2);
			let total: number = 0;
			avgValues.map(x => x[1] / x[2]).forEach(x => total += x);
			if (data.length > 0) {
				this.totalDeltaData.push({
					timestamp: timestamp,
					sampleDate: new Date(timestamp).toString(),
					...{ "Total_DELTA": total }
				});
			}
		}
	}

	/**
	 * totaliseCalculatedData
	 * @param FIVE_MINUTES FIVE_MINUTES 
	 */
	totaliseCalculatedData(FIVE_MINUTES: number) {
		for (let start: number = this.startDate.getTime(); start < this.endDate.getTime(); start += FIVE_MINUTES) {
			const end = start + FIVE_MINUTES;
			let data = this.calculatedData.filter((x: ISensorData) => x.timestamp < end && x.timestamp > start);
			let avgValues: [string, number, number][] = [];
			data.forEach((y: ISensorData) => {
				Object.entries(y).forEach(item => {
					if (this.regexCalc.test(item[0])) {
						let existingItem = avgValues.find(av => av[0] == item[0]);
						if (existingItem) {
							existingItem[1] += <number>item[1];
							existingItem[2]++;
						} else {
							avgValues.push([item[0], <number>item[1], 1]);
						}
					}
				});
			});
			let timestamp = end - ((end - start) / 2);
			let total: number = 0;
			avgValues.map(x => x[1] / x[2]).forEach(x => total += x);
			if (data.length > 0) {
				this.totalCaluclatedData.push({
					timestamp: timestamp,
					sampleDate: new Date(timestamp).toString(),
					...{ "Total_CALCULATED": total }
				});
			}
		}
	}

	/**
	 * calculatedDeltaMinMax
	 */
	calculateDeltaMinMax() {
		this.totalDeltaData.forEach(x => {
			let value = <number>x["Total_DELTA"];
			if (value != undefined) {
				if (value < this.deltaMin) {
					this.deltaMin = value;
				}
				if (value > this.deltaMax) {
					this.deltaMax = value;
				}
			}
		});
		this.deltaMax = parseFloat(this.deltaMax.toFixed(1));
		this.deltaMin = parseFloat(this.deltaMin.toFixed(1));
	}

	/**
	 * setCalculatedAverages
	 */
	setCalculatedAverages() {
		const maxMin = this.getKWHDataMaxMin();
		this.deltaMax = parseFloat(maxMin.max.toFixed(1));
		this.deltaMin = parseFloat(maxMin.min.toFixed(1));
		this.totalAvgPeriod = parseFloat(maxMin.avg.toFixed(3));
		this.totalAvgRunning = parseFloat(maxMin.runningAvg.toFixed(3));
	}

	/**
	 * totaliseData
	 * @param data data 
	 * @param label label 
	 */
	totaliseData(data: ISensorData[], interval: number, inputLabel: string, outputLabel: string): ISensorData[] {
		let result: ISensorData[] = [];
		for (let start = data[0].timestamp; start < data[data.length - 1].timestamp; start += interval) {
			const end = start + interval;
			const windowTotal = data.filter((x: ISensorData) => x.timestamp > start && x.timestamp <= end)
				.map((y: ISensorData) => <number>y[inputLabel])
				.reduce((acc: number, curr: number) => acc += curr, 0);
			const timestamp = end - (interval / 2);
			result.push({
				timestamp: timestamp,
				sampleDate: new Date(timestamp).toString(),
				[outputLabel]: windowTotal
			})
		}
		return result;
	}

	/**
	 * totaliseCumulativeData
	 * @param FIVE_MINUTES FIVE_MINUTES 
	 */
	totaliseCumulativeData_old(FIVE_MINUTES: number) {
		for (let start: number = this.startDate.getTime(); start < this.endDate.getTime(); start += FIVE_MINUTES) {
			const end = start + FIVE_MINUTES;
			let data = this.parsedData.filter((x: ISensorData) => x.timestamp < end && x.timestamp > start);
			let avgValues: [string, number, number][] = [];
			data.forEach((y: ISensorData) => {
				Object.entries(y).forEach(item => {
					if (this.regex.test(item[0])) {
						let existingItem = avgValues.find(av => av[0] == item[0]);
						if (existingItem) {
							existingItem[1] += <number>item[1];
							existingItem[2]++;
						} else {
							avgValues.push([item[0], <number>item[1], 1]);
						}
					}
				});
			});
			let timestamp = end - ((end - start) / 2);
			let total: number = 0;
			avgValues.map(x => x[1] / x[2]).forEach(x => total += x);
			let isAbove: boolean = this.totalCumulativeData.length > 0 ?
				((total ?? 0) > <number>this.totalCumulativeData[this.totalCumulativeData.length - 1]["Total"]) : true;
			if (data.length > 0 && isAbove) {
				this.totalCumulativeData.push({
					timestamp: timestamp,
					sampleDate: new Date(timestamp).toString(),
					...{ "Total": total }
				});
			}
		}
	}

	/**
	 * totaliseCumulativeData
	 * @param FIVE_MINUTES FIVE_MINUTES 
	 */
	totaliseCumulativeData(interval: number) {
		let seperatedCumulativeData: { datapoint: AssetDataPoint, data: ISensorData[] }[] = [];
		this.includedDatapoints.forEach((datapoint: AssetDataPoint) => {
			let bucketedDpData: ISensorData[] = [];
			for (let start: number = this.startDate.getTime(); start < this.endDate.getTime(); start += interval) {
				const end = start + interval;
				let data = this.parsedData.filter((x: ISensorData) => x.timestamp < end && x.timestamp > start);
				let reading: any = undefined;
				data.forEach((dataItem: ISensorData) => {
					Object.entries(dataItem).forEach(entry => {
						if (this.regex.test(entry[0]) && entry[0] == `[${datapoint.DataPointID}]`) {
							if (reading == undefined) {
								reading = dataItem;
							} else if (reading.timestamp < dataItem.timestamp) {
								reading = dataItem;
							}
						}
					});
				});
				if (reading != undefined) {
					bucketedDpData.push({
						timestamp: reading.timestamp,
						sampleDate: reading.sampleDate,
						bucketed_data: reading[`[${datapoint.DataPointID}]`]
					});
				}
			}
			seperatedCumulativeData.push({ datapoint: datapoint, data: bucketedDpData });
		});

		let minLength: number = Math.min(...seperatedCumulativeData.map((x) => x.data.length));

		for (let i: number = 0; i < minLength; i++) {
			let total: number = 0;
			let timestamp: number = seperatedCumulativeData[0].data[i].timestamp;
			seperatedCumulativeData.forEach((x) => {
				if (x.data[i]) {
					total += <number>(x.data[i]["bucketed_data"] ?? 0);
				}
			});
			this.totalCumulativeData.push({
				timestamp: timestamp,
				sampleDate: new Date(timestamp).toString(),
				...{ "Total": total }
			});
		}
	}

	/**
	 * getLatestValue
	 * @param data data 
	 * @param label label 
	 */
	getLatestValue(data: ISensorData[], label: string) {
		this.recentRunningValue = <number>data[data.length - 1][label] ?? 0;
		this.recentRunningValue = parseFloat(this.recentRunningValue.toFixed(3));
	}

	/**
	 * getLatestValue_old - currently not used
	 */
	getLatestValue_old() {
		let seenDPsRecent: string[] = [];
		for (let i: number = 0; i < 10; i++) {
			Object.entries(this.parsedData[this.parsedData.length - (i + 1)]).forEach(item => {
				if (this.regexDelta.test(item[0]) && !seenDPsRecent.includes(item[0])) {
					seenDPsRecent.push(item[0]);
					this.recentRunningValue += <number>item[1];
				}
			});
		}
		this.recentRunningValue = parseFloat(this.recentRunningValue.toFixed(3));
	}

	/**
	 * resetValues
	 */
	resetValues() {
		this.totalDeltaData = [];
		this.totalCumulativeData = [];
		this.totalCaluclatedData = [];
		this.calculatedData = [];
		this.assetRelevantData = [];
		this.assetEnergyMetrics = [];
		this.recentRunningValue = 0;
		this.deltaBarData = [];

		this.deltaMax = 0;
		this.deltaMin = 0;
		this.totalMax = 0;
		this.totalMin = 0;
	}

	/**
	 * toggleShowDeltaBars
	 */
	toggleShowDeltaBars() {
		this.showDeltaChartBars = !this.showDeltaChartBars;
		if (this.showDeltaChartBars) {
			this.trendDeltaBars();
		} else {
			if (this.isKWHDashboard) {
				this.trendkWHValues();
			} else {
				this.trendDeltaValues();
			}
		}
	}

	/**
	 * calculateDeltaChartBarData
	 * @param data data 
	 */
	calculateDeltaChartBarData(data: ISensorData[], label: string) {
		let numWindows: number;
		let hourSeparator: boolean = false;
		const DAY = (24 * 60 * 60 * 1000);
		const HOUR = DAY / 24;
		if (this.strDuration != "custom") {
			const days = parseInt(this.strDuration.split(":")[0]);
			const hours = parseInt(this.strDuration.split(":")[1]);
			numWindows = days != 0 ? days > 2 ? days : days * 24 : hours;
			hourSeparator = days <= 2;
		} else {
			numWindows = this.numDays > 2 ? this.numDays : this.numDays * 24;
			hourSeparator = this.numDays <= 2;
		}
		for (let i: number = 0; i < numWindows; i++) {
			const start = i == 0 ? this.startDate.getTime() : this.startDate.getTime() + (!hourSeparator ? DAY * (i + 1) : HOUR * (i + 1));
			const end = !hourSeparator ? start + DAY : start + HOUR;
			const windowData: number[] = data.filter((x: ISensorData) => x.timestamp > start && x.timestamp <= end).map((x: ISensorData) => <number>x[label]);
			let totalForWindow: number = 0;
			windowData.forEach((x: number) => totalForWindow += x);
			let timestamp = end - (!hourSeparator ? DAY / 2 : HOUR / 2);
			let tsDate: Date = new Date(timestamp);
			if (!hourSeparator) {
				tsDate.setHours(12);
			}
			tsDate.setMinutes(0);
			tsDate.setSeconds(0);
			timestamp = tsDate.getTime();
			this.deltaBarData.push({
				timestamp: timestamp,
				sampleDate: new Date(timestamp).toString(),
				BAR_DATA: totalForWindow
			});
		}
		// console.log(this.deltaBarData.reduce((acc: number, curr: ISensorData) => acc + <number>curr["BAR_DATA"], 0));
		// console.log(this.totalDeltaData.reduce((acc: number, curr: ISensorData) => acc + <number>curr["Total_DELTA"], 0));
		// console.log(this.parsedData
		// 	.map((x: ISensorData) =>
		// 		<number>Object.entries(x).find((y) =>
		// 			this.regexDelta.test(y[0]))?.[1]).reduce((acc: number, curr: number) => acc + curr, 0));
	}

	/**
	 * getAllDataForRegex
	 * @param data data 
	 * @param regex regex 
	 * @param label label  
	 */
	getAllDataForRegex(data: ISensorData[], regex: RegExp, label: string): ISensorData[] {
		return data.filter((x: ISensorData) => Object.entries(x).some(y => regex.test(y[0]))).map((x: ISensorData) => {
			let result: ISensorData = {
				timestamp: x.timestamp,
				sampleDate: x.sampleDate
			}
			let totalDelta: number = 0;
			Object.entries(x).forEach(item => {
				if (regex.test(item[0])) {
					totalDelta += <number>item[1];
				}
			});
			result[label] = totalDelta;
			return result;
		});
	}

	/**
	 * getOrganisationEnergyStructure
	 */
	getOrganisationEnergyStructure() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.organisationDataService.getOrganisationEnergyStructure$(this.accountGUID, this.organisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((structure) => !!structure),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (structure) => {
				this.energyStructure = JSON.parse(structure);
				this.selectedLocation = this.energyStructure[0];
				this.updateTree();
				this.selectInitialNode();
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.organisationDataService.getOrganisationEnergyStructure$:", error);
				}
			},
		});
	}

	/**
	 * addListItemsAsChildren
	 * @param data
	 * @returns
	 */
	addListItemsAsChildren(data: OrganisationsHierarchy[]): OrganisationsHierarchy[] {

		/**
		 * Internal Function
		 * @param data
		 */
		const transformListItems = (data: any[]) => {
			for (let i = 0; i < data.length; i++) {

				if (data[i].concatenatedText == undefined) {
					let _Name = data[i].Name;
					let _FunctionalLocation = data[i].FunctionalLocation;
					if (!!_FunctionalLocation) { _Name = `${_Name} (${_FunctionalLocation})`; }
					data[i].concatenatedText = _Name;
				}

				if (data[i].Assets) {
					data[i].SubChild = data[i].SubChild || [];
					data[i].Assets.forEach((item: any) => {
						if (!item.Deleted) {
							let _Name = item["Name"];
							let _Description = item["Description"];
							if (_Name != _Description && !!_Description) { _Name = `${_Name} - ${_Description}`; }
							item["concatenatedText"] = _Name;
							item["Uid"] = <string>item["AssetID"];
							item["LocationType"] = 'e-icons e-folder';
							data[i].SubChild.push(item);
						}
					});
					data[i].Assets = []; // Clear items after moving them to SubChild
				}
				if (data[i].SubChild) {
					transformListItems(data[i].SubChild);
				}
			}
		};

		transformListItems(data);

		return data;
	}

	/**
	 * Check if string can be parsed as int
	 * @param str
	 * @returns
	 */
	isParsableAsNumber(str: any): boolean {
		const parsed = Number(str);
		return !isNaN(parsed);
	}

	/**
	 * Update Tree
	 */
	updateTree() {
		this.energyStructure = this.addListItemsAsChildren(this.energyStructure);

		// this.treeData[0].Expanded = true;

		this.fields = {
			dataSource: this.energyStructure,
			id: 'Uid',
			text: 'concatenatedText',
			child: 'SubChild',
			// iconCss: 'LocationType',
			// imageUrl: 'ImageURL',
			expanded: 'Expanded',
			hidden: 'Hidden',
			parentUID: 'ParentUid'
		};

		if (this.treeview) {
			this.treeview.dataBind();
			this.treeview.refresh();
		}
	}

	/**
	 * Remember the expanded state of the Nodes in the tree
	 * @param args
	 */
	nodeExpanded(args: NodeExpandEventArgs) {
		let node = this.flattenSubLocations(this.energyStructure).find((x) => x.Uid == args.nodeData["id"]);
		if (node) {
			node.Expanded = <boolean>args.nodeData["expanded"];
		}
		setTimeout(() => {
			this.updateTree();
		}, 0);
	}

	/**
	 * flattenSubLocations
	 * @param hierarchy hierarchy 
	 * @returns 
	 */
	flattenSubLocations(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.SubChild && item.SubChild.length > 0) {
				result = result.concat(item.SubChild);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenSubLocations(item.SubChild));
		}

		return result;
	}

	/**
	 * checkLocationSelected
	 * @param node node 
	 * @returns 
	 */
	checkLocationSelected(node: IOrganisationsHierarchy): boolean {
		let result: boolean = false;
		if (this.selectedLocation) {
			result = this.selectedLocation.Uid == node.Uid;
		}
		return result;
	}

	/**
	 * selectNodeOnTree
	 */
	selectInitialNode() {
		if (this.treeview) {
			setTimeout(() => {
				this.treeview.selectedNodes = [this.selectedLocation.Uid];
			}, 10);
		}
	}

	/**
	 * selectNode
	 * @param event event  
	 */
	selectNode(event: any) {
		if (event.nodeData["id"] != this.selectedLocation.Uid) {
			let loc = this.flattenSubLocations(this.energyStructure).find((x) => x.Uid == event.nodeData["id"]);
			if (loc) {
				this.selectedLocation = loc;
			}
		}
		this.getIncludedDatapoints(this.selectedLocation);
		let sortedIncluded: string = this.includedDatapoints.sort((a: AssetDataPoint, b: AssetDataPoint) => a.DataPointID - b.DataPointID)
			.map((x: AssetDataPoint) => x.DataPointID).toString();
		let sortedLastFetched: string = this.justFetchedDatapoints.sort((a: AssetDataPoint, b: AssetDataPoint) => a.DataPointID - b.DataPointID)
			.map((x: AssetDataPoint) => x.DataPointID).toString();
		if (sortedIncluded != sortedLastFetched) {
			this.getDataForDPs();
		}
	}

	/**
	 * getIncludedDatapoints
	 * @param location location 
	 */
	getIncludedDatapoints(location: IOrganisationsHierarchy) {
		this.includedDatapoints = [];
		if (!this.isParsableAsNumber(location.Uid)) {
			let list: IOrganisationsHierarchy[] = this.flattenSubLocations([location]);
			list.forEach((x: IOrganisationsHierarchy) => {
				if (this.isParsableAsNumber(x.Uid)) {
					let dpAsset = this.energyAssets.find((y) => y.Asset.AssetID == parseInt(x.Uid));
					if (dpAsset) {
						this.includedDatapoints.push(...dpAsset.Datapoints);
					}
				}
			});
		} else {
			let dpAsset = this.energyAssets.find((y) => y.Asset.AssetID == parseInt(location.Uid));
			if (dpAsset) {
				this.includedDatapoints = dpAsset.Datapoints;
			}
		}
	}

	/**
	 * getDataForDPs
	 */
	getDataForDPs() {
		this.endDate = new Date();
		this.startDate = new Date();
		if (this.numDays >= 1) {
			this.startDate.setDate(this.endDate.getDate() - this.numDays);
		} else {
			this.startDate.setHours(this.endDate.getHours() - this.numDays * 24);
		}
		let datapointIDs: string = this.includedDatapoints.map((x: AssetDataPoint) => x.DataPointID).toString();
		this.GetEnergyData(this.startDate.getTime(), this.endDate.getTime(), false, datapointIDs);
	}

	/**
	 * checkEnergyAssetDatapointsIncluded
	 * @param energyAsset energyAsset 
	 * @returns 
	 */
	checkEnergyAssetDatapointsIncluded(energyAsset: { Asset: LevelAsset, Datapoints: AssetDataPoint[] }): boolean {
		let result: boolean = false;
		energyAsset.Datapoints.forEach((x: AssetDataPoint) => {
			if (this.includedDatapoints.some((y: AssetDataPoint) => y.DataPointID === x.DataPointID)) {
				result = true;
			}
		});
		return result;
	}

	saveDashboard() {
		let panelModel: PanelModel[] = this.dashboardLayout.serialize();

		const panels = document.querySelectorAll('.e-panel');
		// assetObjects[0].getHTML();
		console.log(panelModel);
	}
}
